import TextStack, {
  TextLabel,
  TextStackItem,
  TextValue
} from '@/components/text-stack';
import { BalanceDto } from '@/models/suba/balances/BalanceDTO.model';
import BalanceService from '@/services/suba/balances/Balance.service';
import formatters from '@/utils/Formatters';
import DownloadDoneOutlinedIcon from '@mui/icons-material/DownloadDoneOutlined';
import { Paper, Stack, SvgIcon, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { FC } from 'react';

type SubAccountBalancesProps = {
  subAccountId: string;
  showConfirmedIcon?: (() => boolean) | boolean;
  onConfirmedIconClick?: (confirmedBalance: string) => void;
  onBalancesLoaded?: (balanceDto: BalanceDto) => void;
  'data-testid-prefix'?: string;
};

export const SubAccountBalances: FC<SubAccountBalancesProps> = props => {
  const fromSubAccountBalancesQuery = useQuery(
    ['BalancesService.get', props.subAccountId],
    () => BalanceService.get(props.subAccountId),
    {
      enabled: Boolean(props.subAccountId),
      onSuccess: props.onBalancesLoaded
    }
  );

  const dataTestIdPrefix = props['data-testid-prefix']
    ? props['data-testid-prefix'] + '-'
    : '';

  return fromSubAccountBalancesQuery.data ? (
    <Paper
      sx={{
        backgroundColor: theme => theme.palette.primary.light,
        padding: 2
      }}
      variant='outlined'>
      <Stack spacing={1}>
        <Typography
          data-testid={`${dataTestIdPrefix}sub-account-id`}
          variant='caption'>
          ID: {props.subAccountId}
        </Typography>
        <TextStack direction='column' spacing={1}>
          <TextStackItem>
            <TextLabel>Confirmed Balance</TextLabel>
            <TextValue
              data-testid={`${dataTestIdPrefix}sub-account-confirmed-balance`}>
              <Stack direction='row' spacing={1.5}>
                {fromSubAccountBalancesQuery.data?.cash.confirmed !== undefined
                  ? formatters.formatDollars(
                      fromSubAccountBalancesQuery.data?.cash.confirmed
                    )
                  : '—'}
                {props.showConfirmedIcon && (
                  <SvgIcon
                    color='primary'
                    cursor='pointer'
                    data-testid='use-from-sub-account-confirmed-balance-button'
                    onClick={() =>
                      props.onConfirmedIconClick(
                        fromSubAccountBalancesQuery.data?.cash.confirmed
                      )
                    }>
                    <DownloadDoneOutlinedIcon />
                  </SvgIcon>
                )}
              </Stack>
            </TextValue>
          </TextStackItem>
          <TextStackItem>
            <TextLabel>Pending Balance</TextLabel>
            <TextValue
              data-testid={`${dataTestIdPrefix}sub-account-pending-balance`}>
              {fromSubAccountBalancesQuery.data?.cash.pending
                ? formatters.formatDollars(
                    fromSubAccountBalancesQuery.data?.cash.pending
                  )
                : '—'}
            </TextValue>
          </TextStackItem>
        </TextStack>
      </Stack>
    </Paper>
  ) : (
    <AccountPlaceholder />
  );
};

const AccountPlaceholder: FC = () => (
  <Paper
    sx={{
      backgroundColor: theme => theme.palette.grey[50],
      padding: 2
    }}
    variant='outlined'>
    <Stack spacing={1}>
      <Typography variant='caption'>—</Typography>
      <TextStack direction='column' spacing={1}>
        <TextStackItem>
          <TextLabel>Confirmed Balance</TextLabel>
          <TextValue>—</TextValue>
        </TextStackItem>
        <TextStackItem>
          <TextLabel>Pending Balance</TextLabel>
          <TextValue>—</TextValue>
        </TextStackItem>
      </TextStack>
    </Stack>
  </Paper>
);
